import { useEffect, useState } from "react";
import { Users, Activity, UtensilsCrossed, Scale } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { UserSheet } from "../components/UserSheet";
import { ListSkeleton } from "../components/ui/Skeleton";
import { PageHeader } from "../components/ui/PageHeader";
import { SectionHeader } from "../components/ui/SectionHeader";
import type { AdminUser } from "@shared/types";
import type { LucideIcon } from "lucide-react";

interface Props {
  currentUserId: number;
}

type AdminStats = { members: number; active_7d: number; food_entries: number; weigh_ins: number };

export function MoreUsers({ currentUserId }: Props) {
  const t = useT();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [stats, setStats] = useState<AdminStats | null>(null);

  // The counters and the list have to move together. Fetching the tiles once on
  // mount left "2 Members" sitting directly above a list containing one, which
  // is the kind of contradiction that makes an admin screen untrustworthy even
  // when the number it was showing had simply gone stale.
  async function load() {
    const [list] = await Promise.all([
      api.listUsers(),
      api.adminStats().then(setStats).catch(() => setStats(null)),
    ]);
    setUsers(list);
  }

  useEffect(() => { load(); }, []);

  function initialsOf(n: string): string {
    return n.trim().slice(0, 1).toUpperCase() || "?";
  }

  return (
    <div className="space-y-4 pt-4 animate-rise">
      <PageHeader back="/more" title={t("users.title")} />

      {stats && (
        <section className="space-y-2">
          <SectionHeader>{t("admin.overview")}</SectionHeader>
          <div className="grid grid-cols-2 gap-3">
            <StatTile icon={Users} tint="#f97316" label={t("admin.members")} value={stats.members} />
            <StatTile icon={Activity} tint="#10b981" label={t("admin.active7")} value={stats.active_7d} />
            <StatTile icon={UtensilsCrossed} tint="#f43f5e" label={t("admin.food_logs")} value={stats.food_entries} />
            <StatTile icon={Scale} tint="#3b82f6" label={t("admin.weighins")} value={stats.weigh_ins} />
          </div>
        </section>
      )}

      {users.length === 0 ? (
        <ListSkeleton rows={3} />
      ) : (
        <div className="bg-surface rounded-2xl">
          {users.map((u, i) => (
            <button
              key={u.id}
              onClick={() => setEditing(u)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left active:bg-surface-strong ${
                i < users.length - 1 ? "border-b border-line" : ""
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-surface-strong flex items-center justify-center text-base font-semibold flex-shrink-0">
                {initialsOf(u.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{u.name}</div>
                <div className="text-sm text-fg-muted truncate">
                  {u.email ?? (u.role === "admin" ? t("users.role_admin") : t("users.role_member"))}
                </div>
              </div>
              <EntitlementBadge ent={u.entitlement} t={t} />
              <span className="text-fg-faint flex-shrink-0">›</span>
            </button>
          ))}
        </div>
      )}

      <UserSheet
        open={editing !== null}
        user={editing}
        currentUserId={currentUserId}
        onSaved={load}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

function StatTile({ icon: Icon, tint, label, value }: {
  icon: LucideIcon; tint: string; label: string; value: number;
}) {
  return (
    <div className="bg-surface rounded-2xl px-4 py-3 flex items-center gap-3">
      <span
        aria-hidden="true"
        className="w-9 h-9 rounded-[10px] flex items-center justify-center shrink-0"
        style={{ background: tint }}
      >
        <Icon size={18} strokeWidth={2.2} className="text-white" />
      </span>
      <div className="min-w-0">
        <div className="text-lg font-semibold tabular-nums leading-tight">{value}</div>
        <div className="text-sm text-fg-muted truncate">{label}</div>
      </div>
    </div>
  );
}

/** Compact premium/free chip for the admin list. */
function EntitlementBadge({ ent, t }: { ent: AdminUser["entitlement"]; t: (k: string) => string }) {
  if (ent.tier !== "premium") {
    return (
      <span className="text-sm px-2 py-0.5 bg-surface-strong rounded-full text-fg-muted flex-shrink-0">
        {t("users.plan_free")}
      </span>
    );
  }
  const lime = ent.source === "comp" || ent.source === "admin";
  return (
    <span className={`text-sm px-2 py-0.5 rounded-full flex-shrink-0 ${
      lime ? "bg-accent-move/20 text-accent-move" : "bg-brand/15 text-brand"
    }`}>
      {t("users.plan_premium")}
    </span>
  );
}
